/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { RadixSelectControl, RadixSelectOption } from "../../ui/radix-select";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenCustomDailog } from "../../../store/helpers/helperReducers";
import { ILanDevice } from "../../../store/api/deviceConfig/types";
import { useLanDeviceData } from "./hooks";
import { MAXBITRATELIST } from "../../../globals/constants";
import { Loader2 } from "lucide-react";

interface IAddEditLanDeviceProps {
  selectedLanDevice?: ILanDevice;
  modelOpenMode?: string;
}

const AddEditLanDevice: React.FC<IAddEditLanDeviceProps> = ({ modelOpenMode = "Add", selectedLanDevice }) => {
  const dispatch = useStoreDispatch();
  const { lanDeviceData, setLanDeviceData, isAddNewLanDevice, submitHandler, onblureHandler, errMsg, setErrMsg, lanDeviceLoader } =
    useLanDeviceData(selectedLanDevice);

  const handleInputChange = (name: string, value: string) => {
    setLanDeviceData({ ...lanDeviceData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setOpenCustomDailog(false));
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        disabled={!lanDeviceData?.index || lanDeviceLoader}
        onClick={submitHandler}
      >
        {lanDeviceLoader && (
          <Loader2 className="h-4 w-4 animate-spin mr-1" />
        )}
        Save
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title={`${isAddNewLanDevice ? "Add" : "Edit"} Lan Device`} footer={footer} width="520px">
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Enable Lan Device */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="enable-lan-device"
              checked={lanDeviceData?.enable === "true"}
              onCheckedChange={(checked: boolean) => {
                setLanDeviceData({ ...lanDeviceData, enable: checked ? "true" : "false" });
              }}
            />
            <Label htmlFor="enable-lan-device" className="cursor-pointer">Enable Lan Device</Label>
          </div>

          {/* Port */}
          <div className="space-y-2">
            <Label htmlFor="lan-port">
              Port <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lan-port"
              name="index"
              type="number"
              required
              disabled={!isAddNewLanDevice}
              value={lanDeviceData?.index}
              onChange={(e) => handleInputChange("index", e.target.value)}
              onBlur={onblureHandler}
            />
            {errMsg?.index && <p className="text-sm text-destructive">{errMsg.index}</p>}
          </div>

          {/* VLAN ID (hidden) */}
          <div className="hidden">
            <Label htmlFor="lan-vlanId">VLAN ID</Label>
            <Input
              id="lan-vlanId"
              name="vlanId"
              disabled
              value={lanDeviceData?.vlanId}
              onChange={(e) => handleInputChange("vlanId", e.target.value)}
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="lan-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lan-name"
              name="name"
              required
              disabled
              value={lanDeviceData?.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              onBlur={onblureHandler}
            />
            {errMsg?.name && <p className="text-sm text-destructive">{errMsg.name}</p>}
          </div>

          {/* Max Bit Rate */}
          <div className="space-y-2">
            <Label htmlFor="maxBitRate">Max Bit Rate</Label>
            <RadixSelectControl
              options={MAXBITRATELIST.map((v) => ({ value: v, label: v }))}
              value={lanDeviceData?.maxBitRate || ""}
              onValueChange={(value) => handleInputChange("maxBitRate", value)}
              className="w-full"
              placeholder="Select max bit rate"
            />
          </div>

          {/* Duplex */}
          <div className="space-y-2">
            <Label htmlFor="duplex">Duplex</Label>
            <RadixSelectControl
              options={[
                { value: "Full", label: "Full" },
                { value: "Half", label: "Half" },
              ]}
              value={lanDeviceData?.duplex || ""}
              onValueChange={(value) => handleInputChange("duplex", value)}
              className="w-full"
              placeholder="Select duplex"
              disabled={!isAddNewLanDevice}
            />
          </div>
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditLanDevice;
